import React, { useState, useEffect, useRef } from 'react';
import { Layout, message } from 'antd';
import { useDashboard } from '../hooks/useDashboard';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardGrid from '../components/dashboard/DashboardGrid';
import VariablesBar from '../components/dashboard/VariablesBar';
import PanelEditor from '../components/dashboard/PanelEditor';
import { dashboardApi } from '../services/dashboardApi';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { useTheme } from '../context/ThemeContext';

const { Content } = Layout;

const AllGraphsTest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  const {
    dashboard,
    addPanel,
    removePanel,
    duplicatePanel,
    updatePanel,
    updatePanelData,
    setPanelLoading,
    setPanelError,
    updateDashboard,
    saveDashboard,
    loadDashboard,
    importDashboard,
    exportDashboard,
    updateTimeRange,
    updateVariable,     
    deleteVariable,
    getVariableValue,   
    initializeVariables,
  } = useDashboard();

  const [editingPanelId, setEditingPanelId] = useState(null);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [selectorRefreshKey, setSelectorRefreshKey] = useState(0);
  const [currentDashboardName, setCurrentDashboardName] = useState(null);
  const dashboardRef = useRef(dashboard);
  dashboardRef.current = dashboard;
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dashboardName = params.get('dashboard');
    const isNew = params.get('new');

    if (dashboardName && !isNew) {
      handleSelectDashboard(dashboardName);
    } else if (dashboardName && isNew === 'true') {
      updateDashboard({
        uid: null,
        title: dashboardName,
        description: '',
        tags: [],
        time: { from: 'now-6h', to: 'now' },
        timezone: 'browser',
        refresh: '',
        panels: [],
        templating: { list: [] },
        variables: {},
      });
      setCurrentDashboardName(dashboardName);
    }
  }, [location.search]);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const response = await dashboardApi.searchMetrics('');
        if (response.success && response.data.metrics) {
          setAvailableMetrics(response.data.metrics);
        }
      } catch (error) {
        // Error loading metrics - optional feature
      }
    };
    loadMetrics();
  }, []);

  useEffect(() => {
    if (dashboard.templating?.list?.length > 0) {
      initializeVariables();
    }
  }, [dashboard.templating?.list?.length, initializeVariables]);

  const handleAddPanel = () => {
    addPanel({
      type: 'timeseries',
      title: 'New Panel',
      gridPos: { x: 0, y: 0, w: 12, h: 6 },
    });
    message.success('Panel added');
  };

  const handleDuplicatePanel = (panelId) => {
    duplicatePanel(panelId);
    message.success('Panel duplicated');
  };

  const handleDeletePanel = (panelId) => {
    removePanel(panelId);
    message.success('Panel deleted');
  };

  const handleRefreshIntervalChange = (value) => {
    updateDashboard({ refresh: value || '' });
  };

  // Grafana-style auto-refresh
  useEffect(() => {
    const interval = dashboard.refresh;
    if (!interval || interval === '') return;

    const ms = (() => {
      const match = interval.match(/^(\d+)(s|m|h)$/);
      if (!match) return 0;
      const num = parseInt(match[1], 10);
      const unit = match[2];
      if (unit === 's') return num * 1000;
      if (unit === 'm') return num * 60 * 1000;
      if (unit === 'h') return num * 3600 * 1000;
      return 0;
    })();

    if (ms <= 0) return;
    const timer = setInterval(() => {
      const d = dashboardRef.current;
      updateDashboard({ ...d, _refreshKey: (d._refreshKey || 0) + 1 });
    }, ms);
    return () => clearInterval(timer);
  }, [dashboard.refresh, updateDashboard]);

  const handleEditPanel = (panelId) => {
    setEditingPanelId(panelId);
  };

  const handleSavePanel = (updatedPanel) => {
    updatePanel(updatedPanel.id, updatedPanel);
    setEditingPanelId(null);
    message.success('Panel updated');
  };

  const handleRefresh = () => {
    updateDashboard({ ...dashboard, _refreshKey: (dashboard._refreshKey || 0) + 1 });
    message.success('Dashboard refreshed');
  };

const handleVariableChange = (variableName, value) => {
  updateDashboard({
    variables: {
      ...dashboard.variables,
        [variableName]: value,
      },
  });
    message.success(`${variableName} updated`);
};

const handleSave = async () => {
  try {
    const result = await saveDashboard();
    
    if (result.success) {
        message.success(`Dashboard saved${result.isNew ? ' (new)' : ''}`);
      setCurrentDashboardName(result.dashboardName);
        setSelectorRefreshKey((prev) => prev + 1);
      
      const params = new URLSearchParams(location.search);
      params.set('dashboard', result.dashboardName);
      params.delete('new');
      navigate(`/graphs-test?${params.toString()}`, { replace: true });
        return result;
    }
      message.error(result.error || 'Failed to save');
      return result;
  } catch (error) {
    message.error(`Failed to save: ${error.message}`);
      return { success: false, error: error.message };
  }
};

const handleLayoutChange = (newLayout) => {
    const updatedPanels = dashboard.panels.map((panel) => {
      const layoutItem = newLayout.find((l) => l.i === panel.id.toString());
    if (layoutItem) {
      return {
        ...panel,
        gridPos: { 
          x: layoutItem.x, 
          y: layoutItem.y, 
          w: layoutItem.w, 
            h: layoutItem.h,
          },
      };
    }
    return panel;
  });
  
  updateDashboard({ ...dashboard, panels: updatedPanels });
};

const handleSelectDashboard = async (dashboardName) => {
  const result = await loadDashboard(dashboardName);
  
  if (result.success) {
    setCurrentDashboardName(dashboardName);
      const params = new URLSearchParams(location.search);
      params.set('dashboard', dashboardName);
      params.delete('new');
      navigate(`/graphs-test?${params.toString()}`, { replace: true });
  } else {
      message.error(result.error || 'Failed to load dashboard');
  }
};

const handleBackToList = () => {
  navigate('/dashboards');
};

const handleCreateNew = () => {
  updateDashboard({
    uid: null,
    title: 'New Dashboard',
      time: { from: 'now-6h', to: 'now' },
      refresh: '',
    panels: [],
    templating: { list: [] },
      variables: {},
  });
  setCurrentDashboardName(null);
    navigate('/graphs-test?new=true');
    message.info('Create a new dashboard');
};

  if (editingPanelId) {
    const panelToEdit = dashboard.panels.find((p) => p.id === editingPanelId);
    
    if (!panelToEdit) {
      setEditingPanelId(null);
      return null;
    }

    return (
      <PanelEditor
        panel={panelToEdit}
        timeRange={dashboard.time}
        variables={dashboard.variables}
        variableDefinitions={dashboard.templating?.list || []}
        onClose={() => setEditingPanelId(null)}
        onSave={handleSavePanel}
        availableMetrics={availableMetrics}
      />
    );
  }

  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: isDark ? '#0d1117' : '#f0f2f5',
      }}
    >
      <DashboardHeader
        dashboard={dashboard}
        currentDashboardName={currentDashboardName} 
        selectorRefreshKey={selectorRefreshKey}
        onSelectDashboard={handleSelectDashboard}   
        onCreateNew={handleCreateNew}
        onUpdateDashboard={updateDashboard}
        onSave={handleSave} 
        onExport={exportDashboard}
        onImport={importDashboard}
        onRefresh={handleRefresh}
        onBackToList={handleBackToList}
        timeRange={dashboard.time}
        onTimeRangeChange={updateTimeRange}
        refreshInterval={dashboard.refresh}
        onRefreshIntervalChange={handleRefreshIntervalChange}
        onAddPanel={handleAddPanel}
      />
    
      <VariablesBar
        variableDefinitions={dashboard.templating?.list || []} 
        variables={dashboard.variables}
        onVariableChange={handleVariableChange}
        onVariableUpdate={updateVariable}
        onVariableDelete={deleteVariable}
      />

      <Content style={{ background: 'transparent' }}>
        <DashboardGrid
          panels={dashboard.panels}
          refreshKey={dashboard._refreshKey}
          onLayoutChange={handleLayoutChange}
          timeRange={dashboard.time}
          variables={dashboard.variables} 
          onAddPanel={handleAddPanel}
          onEditPanel={handleEditPanel}
          onDeletePanel={handleDeletePanel}
          onDuplicatePanel={handleDuplicatePanel}
          onUpdatePanelData={updatePanelData}
          onSetPanelLoading={setPanelLoading}
          onSetPanelError={setPanelError}
        />
      </Content>
    </Layout>
  );
};

export default AllGraphsTest;
